import type { ReactNode } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { cn } from '../../lib/cn'
import { isFirebaseConfigured } from '../../lib/firebase'
import { useAuth } from '../../lib/auth/useAuth'
import { ThemeToggle } from './ThemeToggle'

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    'text-sm transition-colors',
    isActive ? 'text-ink' : 'text-ink-muted hover:text-ink',
  )

// App shell: a slim header with the wordmark, centered nav, account, and theme.
export function AppLayout({ children }: { children: ReactNode }) {
  const { user, signOutUser } = useAuth()

  return (
    <div className="min-h-svh overflow-x-hidden">
      <header className="border-b border-border">
        <div className="mx-auto grid max-w-5xl grid-cols-[1fr_auto_1fr] items-center gap-2 px-4 py-4 sm:gap-4 sm:px-6">
          <Link
            to="/"
            className="flex min-w-0 items-center gap-2 justify-self-start text-lg font-semibold tracking-tight"
          >
            <img
              src="/logo-64.png"
              alt=""
              width={24}
              height={24}
              className="h-6 w-6 shrink-0"
            />
            {/* Logo-only on very small screens so the row never overflows. */}
            <span className="hidden xs:inline">
              just<span className="text-known">unstuck</span>
            </span>
          </Link>

          <nav className="flex items-center gap-4 justify-self-center sm:gap-6">
            <NavLink to="/" end className={navLinkClass}>
              Home
            </NavLink>
            <NavLink to="/history" className={navLinkClass}>
              History
            </NavLink>
          </nav>

          <div className="flex items-center gap-3 justify-self-end sm:gap-4">
            {isFirebaseConfigured && user && (
              <button
                type="button"
                onClick={() => void signOutUser()}
                className="cursor-pointer text-sm whitespace-nowrap text-ink-muted transition-colors hover:text-ink"
              >
                Sign out
              </button>
            )}
            <ThemeToggle />
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-4 py-10 sm:px-6">{children}</main>
    </div>
  )
}
